import { ScreenContainer } from "../../components";
import { ScreenProps } from "./types";
import { ProgressBar } from "react-native-paper";
import { View, Text, Platform } from "react-native";
import ActionsContainer from "../../components/ActionsContainer";
import { ScrollableContainer } from "../../components";
import Button from "../../components/Button";
import { colors } from "../../../theme";
import { gql, useQuery } from "@apollo/client";
import moment from "moment";
import { useFeatureFlags } from "../../hooks/useFlagr";

export const DUES_QUERY = gql`
  query GetDuesOrder($orderId: ID) {
    duesCycle: currentDuesCycle(orderId: $orderId) {
      id
      startsOn
      endsOn
      order {
        id
        dueDate
      }
    }
  }
`;

export interface QueryType {
  duesCycle: {
    order: {
      dueDate: string;
    };
    startsOn: string;
    endsOn: string;
  };
}

function DuesOverviewScreen({ navigation, route }: ScreenProps<"DuesOverview" | "DuesProxyOverview">) {
  const flags = useFeatureFlags();
  const flagActive = flags["dues-order-lookup"];
  const { data } = useQuery<QueryType>(DUES_QUERY, {
    ...(flagActive && { variables: { orderId: route.params?.orderId } }),
  });
  const assignProxy = route.name === "DuesProxyOverview";

  function onContinue() {
    navigation.navigate(assignProxy ? "DuesProxyReview" : "DuesReview", {
      ...(flagActive && { orderId: route.params?.orderId }),
    });
  }

  function getYear(date: string) {
    return moment(date).year();
  }
  const years = data?.duesCycle ? `${getYear(data?.duesCycle?.startsOn)}-${getYear(data?.duesCycle?.endsOn)}` : null;

  return (
    <ScreenContainer navigationTitle="Dues Payment">
      {Platform.OS !== "web" && <ProgressBar progress={0.25} color={colors.primary.light} />}
      <ScrollableContainer containerClassName="px-7 py-9">
        <Text className="text-ink-darkest text-title-4 font-700">
          {assignProxy ? `${years} Dues Proxy Payment Request` : `${years} Dues Payment`}
        </Text>
        <Text className="mt-3 text-regular-normal font-400 text-ink-dark">
          {assignProxy
            ? "The dues proxy payment request allows you to assign access to an individual (such as an employer, supervisor or agent) so that they may pay dues on your behalf.  The process should take just a few minutes to complete, and consists of the following steps:"
            : "The dues payment process should take just a few minutes to complete, and consists of the following steps:"}
        </Text>
        {["Verify Information", "Dues Summary", ...(assignProxy ? ["Proxy Information"] : ["Payment"])].map((step, i) => (
          <View key={i} className="flex-row items-center mt-4">
            <View className="items-center justify-center w-6 h-6 mr-3 border-2 rounded-full border-ink">
              <Text className="font-700 text-ink-dark">{i + 1}</Text>
            </View>
            <Text className="text-regular-normal font-700">{step}</Text>
          </View>
        ))}
        <View className="my-8 border-t border-sky" />
        <Text className="mb-4 font-700 text-large-tight">Things to know</Text>
        <View className="mb-14">
          {[
            `Pay any time through ${moment(data?.duesCycle?.order?.dueDate).format(
              "MMMM D, YYYY"
            )} without penalty or late fee.`,
            'Your current employment records must be up-to-date for accurate dues amounts. (You will have an opportunity to review and make corrections when you tap "Continue" below.)',
            ...(!assignProxy
              ? ["Payments made by credit card and other credit methods are subject to a 3% convenience fee."]
              : []),
          ].map((info, i) => (
            <View key={i} className="flex-row items-start">
              <Text className="text-regular-normal font-400 mr-2">•</Text>
              <Text className="text-regular-normal font-400 text-ink-dark">{info}</Text>
            </View>
          ))}
        </View>
      </ScrollableContainer>
      <ActionsContainer>
        <Button mode="contained" onPress={onContinue}>
          Continue
        </Button>
      </ActionsContainer>
    </ScreenContainer>
  );
}

export default DuesOverviewScreen;
